import { useEffect, useRef } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Input } from '@/components/atoms/input';
import { cn } from '@/lib/utils';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  GOOGLE_MAPS_VERSION,
} from '@/lib/google-maps/libraries';

interface ParsedAddress {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

interface GooglePlacesInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (parsed: ParsedAddress) => void;
  placeholder?: string;
  className?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
}

function parsePlace(place: google.maps.places.PlaceResult): ParsedAddress {
  const components = place.address_components ?? [];
  const get = (type: string) => components.find((c) => c.types.includes(type))?.long_name ?? '';
  const getShort = (type: string) =>
    components.find((c) => c.types.includes(type))?.short_name ?? '';

  const streetNumber = get('street_number');
  const route = get('route');
  const street2 = get('subpremise');
  const street = [streetNumber, route].filter(Boolean).join(' ');

  return {
    street,
    street2,
    city: get('locality') || get('sublocality') || get('postal_town'),
    state: getShort('administrative_area_level_1'),
    zip: get('postal_code'),
  };
}

export function GooglePlacesInput({
  id,
  value,
  onChange,
  onAddressSelect,
  placeholder = '123 Main St',
  className,
  ariaInvalid,
  ariaDescribedBy,
}: GooglePlacesInputProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onChangeRef = useRef(onChange);
  const onAddressSelectRef = useRef(onAddressSelect);

  // Keep refs current without re-creating the autocomplete listener
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['us'] },
      fields: ['address_components', 'formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) return;
      const parsed = parsePlace(place);
      onChangeRef.current(parsed.street);
      onAddressSelectRef.current?.(parsed);
    });

    autocompleteRef.current = autocomplete;

    return () => {
      google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [isLoaded]);

  return (
    <Input
      ref={inputRef}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(className)}
      autoComplete="new-password"
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
    />
  );
}
